// src/gestion-logistics/components/AccountMasterFormModal.tsx
import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Row,
  Col,
  Alert,
  Space,
} from "antd";
import {
  DollarOutlined,
  FileTextOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { useAccountMasters } from "../hooks/useAccountMasters";
import {
  AccountMaster,
  AccountMasterFormData,
  AccountTypeCode,
} from "../types/costing.types";

const { Option } = Select;
const { TextArea } = Input;

interface AccountMasterFormModalProps {
  visible: boolean;
  onClose: () => void;
  accountMaster?: AccountMaster | null;
  mode: "create" | "edit";
}

/**
 * Modal de formulaire pour créer/éditer une rubrique comptable
 */
const AccountMasterFormModal: React.FC<AccountMasterFormModalProps> = ({
  visible,
  onClose,
  accountMaster,
  mode,
}) => {
  const [form] = Form.useForm();
  const { createAccountMaster, updateAccountMaster, accountTypes } =
    useAccountMasters();
  const [loading, setLoading] = useState(false);
  const [selectedAccountType, setSelectedAccountType] = useState<
    AccountTypeCode | undefined
  >(undefined);

  useEffect(() => {
    if (visible && accountMaster && mode === "edit") {
      form.setFieldsValue({
        ...accountMaster,
      });
      setSelectedAccountType(accountMaster.account_type_code);
    } else if (visible && mode === "create") {
      form.resetFields();
      form.setFieldsValue({
        is_active: true,
        tax_applicable: false,
      });
      setSelectedAccountType(undefined);
    }
  }, [visible, accountMaster, mode, form]);

  const handleAccountTypeChange = (value: AccountTypeCode) => {
    setSelectedAccountType(value);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Trouver l'account_type_id depuis le code
      const selectedType = accountTypes.find(
        (type) => type.code === values.account_type_code
      );

      if (!selectedType) {
        console.error("Type de compte non trouvé");
        setLoading(false);
        return;
      }

      const formData: AccountMasterFormData = {
        account_type_id: selectedType.id,
        code: values.code.trim().toUpperCase(),
        name: values.name.trim(),
        description: values.description?.trim(),
        default_unit: values.default_unit?.trim(),
        default_unit_price: values.default_unit_price || 0,
        tax_applicable: values.tax_applicable || false,
        is_active: values.is_active !== undefined ? values.is_active : true,
      };

      let success = false;

      if (mode === "create") {
        const result = await createAccountMaster(formData);
        success = result !== null;
      } else if (accountMaster) {
        success = await updateAccountMaster(accountMaster.id, formData);
      }

      if (success) {
        form.resetFields();
        onClose();
      }
    } catch (error) {
      console.error("[LOGISTICS] Form validation failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const getAccountTypeInfo = (code: AccountTypeCode) => {
    switch (code) {
      case AccountTypeCode.INCOME:
        return {
          icon: <DollarOutlined />,
          color: "#389e0d",
          description: "Revenus facturés au client (freight charges, handling fees, etc.)",
        };
      case AccountTypeCode.EXPENSE:
        return {
          icon: <FileTextOutlined />,
          color: "#d46b08",
          description: "Dépenses internes non refacturées (salaires, loyers, etc.)",
        };
      case AccountTypeCode.DISBURSEMENT:
        return {
          icon: <SwapOutlined />,
          color: "#1890ff",
          description: "Débours avancés et refacturés au client (customs fees, port charges, etc.)",
        };
      default:
        return null;
    }
  };

  const accountTypeInfo = selectedAccountType
    ? getAccountTypeInfo(selectedAccountType)
    : null;

  return (
    <Modal
      title={
        mode === "create"
          ? "Nouvelle Rubrique Comptable"
          : "Modifier la Rubrique Comptable"
      }
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={700}
      okText={mode === "create" ? "Créer" : "Enregistrer"}
      cancelText="Annuler"
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        {/* Type de Compte */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="Type de Compte"
              name="account_type_code"
              rules={[
                { required: true, message: "Le type de compte est requis" },
              ]}
              tooltip="Choisissez INCOME pour les revenus, EXPENSE pour les dépenses internes, DISBURSEMENT pour les débours refacturés"
            >
              <Select
                placeholder="Sélectionner le type de compte"
                onChange={handleAccountTypeChange}
                disabled={mode === "edit"} // Ne pas permettre de changer le type en édition
              >
                {accountTypes.map((type) => (
                  <Option key={type.code} value={type.code}>
                    <Space>
                      {type.code === AccountTypeCode.INCOME && (
                        <DollarOutlined style={{ color: "#389e0d" }} />
                      )}
                      {type.code === AccountTypeCode.EXPENSE && (
                        <FileTextOutlined style={{ color: "#d46b08" }} />
                      )}
                      {type.code === AccountTypeCode.DISBURSEMENT && (
                        <SwapOutlined style={{ color: "#1890ff" }} />
                      )}
                      {type.name}
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Info sur le type sélectionné */}
        {accountTypeInfo && (
          <Alert
            message={accountTypeInfo.description}
            type="info"
            showIcon
            icon={accountTypeInfo.icon}
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Code et Nom */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Code"
              name="code"
              rules={[
                { required: true, message: "Le code est requis" },
                {
                  pattern: /^[A-Z0-9_-]+$/,
                  message: "Majuscules, chiffres, - et _ uniquement",
                },
                {
                  max: 20,
                  message: "Maximum 20 caractères",
                },
              ]}
              tooltip="Code unique pour identifier la rubrique (ex: FRET-MAR, CUSTOMS, INSURANCE)"
            >
              <Input
                placeholder="Ex: FRET-MAR"
                maxLength={20}
                style={{ textTransform: "uppercase" }}
              />
            </Form.Item>
          </Col>

          <Col span={16}>
            <Form.Item
              label="Nom de la Rubrique"
              name="name"
              rules={[
                { required: true, message: "Le nom est requis" },
                { max: 100, message: "Maximum 100 caractères" },
              ]}
            >
              <Input
                placeholder="Ex: Fret Maritime"
                maxLength={100}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Description */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="Description"
              name="description"
              tooltip="Description détaillée de la rubrique (optionnel)"
            >
              <TextArea
                rows={2}
                placeholder="Description de la rubrique..."
                maxLength={500}
                showCount
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Unité par défaut et Prix unitaire */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Unité par Défaut"
              name="default_unit"
              tooltip="Unité de mesure par défaut (ex: container, kg, m³, TEU)"
            >
              <Input placeholder="Ex: container, kg, m³" maxLength={20} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Prix Unitaire par Défaut"
              name="default_unit_price"
              tooltip="Prix unitaire par défaut qui sera proposé lors de la création d'une ligne de costing"
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={0.01}
                placeholder="0.00"
                prefix={<DollarOutlined />}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Options */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Taxe Applicable"
              name="tax_applicable"
              valuePropName="checked"
              tooltip="Indiquer si cette rubrique est soumise à la taxe (TVA, VAT, etc.)"
            >
              <Switch
                checkedChildren="Oui"
                unCheckedChildren="Non"
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Statut"
              name="is_active"
              valuePropName="checked"
              tooltip="Les rubriques inactives n'apparaissent pas dans les sélections"
            >
              <Switch
                checkedChildren="Actif"
                unCheckedChildren="Inactif"
              />
            </Form.Item>
          </Col>
        </Row>

        {mode === "edit" && (
          <Alert
            message="Modification de rubrique"
            description="Le type de compte ne peut pas être modifié après création. Les lignes de job costing existantes utilisant cette rubrique conservent leurs valeurs."
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Form>
    </Modal>
  );
};

export default AccountMasterFormModal;
